import { Alert, Box, Divider, useTheme } from '@mui/material';
import StepPdfSource from 'components/@home/@documents/SentDocuments/edit-steps/StepPdfSource';
import SetupDocumentPalette from 'components/@home/@documents/SentDocuments/edit-steps/SetupDocumentPalette';
import WidgetCanvas from 'components/@home/@documents/SentDocuments/WidgetCanvas';
import { buildPlacementField } from 'components/@home/drawers/AcknowledgeCreateDrawer/Form/buildPlacementField';
import {
  getBoxItemNormalizedTextOnPage,
  getRoutingBoxPreviewTexts,
  inferRoutingBoxPositionFromViewportBox,
  scanBoxItemAcrossPdfPages,
} from 'components/@home/drawers/HrBotDrawer/pdf-utils';
import { array, bool, func, number, object, string } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import authCompany from 'store/selectors/authCompany';
import {
  getOmmnioSignMarketingSignUrl,
  isActiveOmmnioSignProvider,
  isOmmnioSignProvider,
} from 'utils/eSignatureProviderAccess';
import { I18n } from 'utils/i18n';

const fieldDefaults = (type, preset) => ({
  mandatory: type === 'signature' || type === 'checkbox',
  editable: true,
  isYesNo: false,
  ...(type === 'date' && !preset.prefillValue ? { prefillValue: { field: 'currentDate' } } : {}),
  ...(preset.prefillValue ? { prefillValue: preset.prefillValue } : {}),
});

/** Non-empty preview string is acceptable for routing overlay / placement sample. */
function routingPreviewAcceptable(_itemName, trimmed) {
  return trimmed.length > 0;
}

/**
 * `template.items[].name` for routing boxes. Employee slot is only ever `code` or `id`, never
 * whatever `template.sendBy` might hold — so it cannot collide with the `dni` box.
 */
function routingPlacementItemName(preset, sendBy) {
  if (preset?.name === 'dni') return 'dni';
  if (preset?.name === 'year') return 'year';
  if (preset?.name === 'month') return 'month';
  if (preset?.name === 'day') return 'day';
  if (preset?.name === 'code') return sendBy === 'id' ? 'id' : 'code';
  return 'code';
}

/** Sidebar + scan toast label for a routing extractor (`template.items[].name`). */
function extractionPaletteLabel(itemName) {
  switch (itemName) {
    case 'dni':
      return I18n.t('HrBot.EditTemplate.SetupDocumentPalette.dni');
    case 'year':
      return I18n.t('HrBot.EditTemplate.SetupDocumentPalette.extractYear');
    case 'month':
      return I18n.t('HrBot.EditTemplate.SetupDocumentPalette.extractMonth');
    case 'day':
      return I18n.t('HrBot.EditTemplate.SetupDocumentPalette.extractDay');
    case 'code':
    case 'id':
    default:
      return I18n.t('HrBot.EditTemplate.SetupDocumentPalette.employeeCode');
  }
}

/** Persisted `template.items[].name` values cleared when switching to collective signing. */
const COLLECTIVE_CLEARED_ROUTING_ITEM_NAMES = new Set([
  'code',
  'id',
  'dni',
  'year',
  'month',
  'day',
]);
/** Palette `preset.name` values for placement mode (always `code`, never `id`). */
const COLLECTIVE_CLEARED_ITEM_PLACEMENT_PRESETS = new Set(['code', 'dni', 'year', 'month', 'day']);

const StepSetupDocument = ({
  template,
  setTemplate,
  fields,
  syncFieldsToTemplate,
  canvasPdfSrc = null,
  uploadBusy = false,
  onPdfDrop,
  onReplacePdf,
  currentSignerIndex,
  setCurrentSignerIndex,
  placeholderMembers,
  canUseXml = false,
  sendSessionPdfGate = false,
}) => {
  const theme = useTheme();
  const company = useSelector(authCompany);
  const locale = useSelector(state => state.i18n?.locale);
  const digitalSignatureFieldsMode = useMemo(() => {
    if (company == null) return 'enabled';
    if (!isOmmnioSignProvider(company)) return 'hidden';
    if (!isActiveOmmnioSignProvider(company)) return 'disabled';
    return 'enabled';
  }, [company]);
  const ommnioSignMarketingUrl =
    digitalSignatureFieldsMode === 'disabled' ? getOmmnioSignMarketingSignUrl(locale) : undefined;

  const [pendingPlacement, setPendingPlacement] = useState(null);
  const [extractionScan, setExtractionScan] = useState(null);
  const [routingItemPreviewText, setRoutingItemPreviewText] = useState({});
  const templateRef = useRef(template);
  templateRef.current = template;
  const scanGenerationRef = useRef(0);
  /** Per routing `item.name` so a scan for `dni` does not cancel in-flight preview updates for `code`. */
  const previewScanGenByItemRef = useRef({});
  /** One debounce timer per `item.name` so moving the code box does not cancel a pending DNI scan (and vice versa). */
  const debounceScanTimersByItemRef = useRef({});
  /** Cancels stale `inferRoutingBoxPositionFromViewportBox` work after rapid drag/resize. */
  const routingPositionRefineGenByItemRef = useRef({});

  useEffect(
    () => () => {
      scanGenerationRef.current += 1;
      previewScanGenByItemRef.current = {};
      routingPositionRefineGenByItemRef.current = {};
      Object.values(debounceScanTimersByItemRef.current).forEach(t => clearTimeout(t));
      debounceScanTimersByItemRef.current = {};
    },
    [],
  );

  useEffect(() => {
    if (template.encryptPdfWithDni) setPendingPlacement(null);
  }, [template.encryptPdfWithDni]);

  /**
   * Hint user how to place routing items (ghost + click) whenever that placement mode starts.
   */
  useEffect(() => {
    if (pendingPlacement?.kind !== 'item') return;
    const routeKey = routingPlacementItemName(pendingPlacement.preset, template.sendBy);
    scanGenerationRef.current += 1;
    setExtractionScan({ status: 'hint', itemName: routeKey });
  }, [pendingPlacement?.kind, pendingPlacement?.preset, template.sendBy]);

  const signingMode = template.signingMode || 'onePerMember';

  useEffect(() => {
    if (signingMode === 'onePerMember') return;
    setTemplate(t => {
      const items = t.items || [];
      const filtered = items.filter(
        i => !(i.selector === 'box' && COLLECTIVE_CLEARED_ROUTING_ITEM_NAMES.has(i.name)),
      );
      let next = t;
      let changed = false;
      if (filtered.length !== items.length) {
        next = { ...next, items: filtered };
        changed = true;
      }
      if (next.sendBy === 'id') {
        next = { ...next, sendBy: 'code' };
        changed = true;
      }
      if (next.encryptPdfWithDni) {
        next = { ...next, encryptPdfWithDni: false };
        changed = true;
      }
      return changed ? next : t;
    });
    setPendingPlacement(p => {
      if (p?.kind === 'item' && COLLECTIVE_CLEARED_ITEM_PLACEMENT_PRESETS.has(p.preset?.name)) {
        return null;
      }
      return p;
    });
  }, [signingMode, setTemplate]);

  const buildFormatForScan = useCallback(itemsList => {
    const t = templateRef.current;
    return {
      type: t.split ? 'single-pdf' : 'multi-pdf',
      items: itemsList,
      sendBy: t.sendBy || 'code',
    };
  }, []);

  const documentShape = template.documentShape || (template.split ? 'personalized' : 'shared');
  const showSharedSigningToolbar =
    digitalSignatureFieldsMode !== 'hidden' &&
    template.isDigitalSignature &&
    documentShape === 'shared';

  const boxRoutingScanKey = useMemo(() => {
    const items = (template.items || []).filter(
      i => i.selector === 'box' && i.coords && i.coords.left != null,
    );
    if (!items.length) return '';
    return items
      .map(i =>
        [
          i.name,
          i.page || 1,
          i.coords.left,
          i.coords.top,
          i.coords.right,
          i.coords.bottom,
          i.position ?? 0,
          i.join ?? '',
        ].join(':'),
      )
      .sort()
      .join('|');
  }, [template.items]);

  useEffect(() => {
    if (!canvasPdfSrc || !boxRoutingScanKey) {
      if (!boxRoutingScanKey) setRoutingItemPreviewText({});
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const res = await fetch(canvasPdfSrc, { credentials: 'same-origin' });
          const buf = await res.arrayBuffer();
          if (cancelled) return;
          const t = templateRef.current;
          const itemsList = Array.isArray(t.items) ? t.items : [];
          const boxItems = itemsList.filter(
            i => i.selector === 'box' && i.coords && i.coords.left != null,
          );
          if (!boxItems.length) return;
          const fmt = buildFormatForScan(itemsList);
          const computed = await getRoutingBoxPreviewTexts(buf, fmt);
          if (!cancelled) {
            const t2 = templateRef.current;
            const list2 = Array.isArray(t2.items) ? t2.items : [];
            const mergedComputed = { ...computed };
            for (const it of list2) {
              if (it?.selector !== 'box' || !it.name) continue;
              const cv =
                typeof mergedComputed[it.name] === 'string'
                  ? mergedComputed[it.name].replace(/\s+/g, ' ').trim()
                  : '';
              if (routingPreviewAcceptable(it.name, cv)) continue;
              const ps =
                typeof it.placementSample === 'string'
                  ? it.placementSample.replace(/\s+/g, ' ').trim()
                  : '';
              if (ps && routingPreviewAcceptable(it.name, ps)) {
                mergedComputed[it.name] = ps;
              }
            }
            setRoutingItemPreviewText(prev => {
              const next = { ...prev };
              for (const it of list2) {
                if (it?.selector !== 'box' || !it.name) continue;
                const name = it.name;
                const val = mergedComputed[name];
                const vt = typeof val === 'string' ? val.replace(/\s+/g, ' ').trim() : '';
                if (vt.length > 0) {
                  next[name] = val;
                  continue;
                }
                const prevVt =
                  typeof next[name] === 'string' ? next[name].replace(/\s+/g, ' ').trim() : '';
                if (prevVt.length === 0) {
                  next[name] = typeof val === 'string' ? val : '';
                }
                // else: keep non-empty preview from `runExtractionScan` — batch often returns '' briefly
                // while pdf.js/text layer catches up, which flipped the overlay green → red.
              }
              return next;
            });
          }
        } catch {
          /* ignore batch refresh */
        }
      })();
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [canvasPdfSrc, boxRoutingScanKey, buildFormatForScan]);

  const routingItemDetection = useMemo(() => {
    const out = {};
    const boxItems = (template.items || []).filter(
      i => i.selector === 'box' && i.coords && i.coords.left != null,
    );
    for (const it of boxItems) {
      if (!Object.prototype.hasOwnProperty.call(routingItemPreviewText, it.name)) {
        out[it.name] = 'pending';
        continue;
      }
      const raw = routingItemPreviewText[it.name];
      const trimmed = typeof raw === 'string' ? raw.replace(/\s+/g, ' ').trim() : '';
      out[it.name] = routingPreviewAcceptable(it.name, trimmed) ? 'found' : 'missing';
    }
    return out;
  }, [template.items, routingItemPreviewText]);

  const runExtractionScan = useCallback(
    async (itemName, itemsList, scanExtras = {}) => {
      if (!canvasPdfSrc || !itemName || !itemsList?.length) return;
      const target = itemsList.find(
        i => i.name === itemName && i.selector === 'box' && i.coords?.left != null,
      );
      if (!target) return;

      const previewGen = (previewScanGenByItemRef.current[itemName] ?? 0) + 1;
      previewScanGenByItemRef.current[itemName] = previewGen;

      const alertGen = ++scanGenerationRef.current;
      setExtractionScan({ status: 'loading', itemName });
      try {
        const res = await fetch(canvasPdfSrc, { credentials: 'same-origin' });
        const buf = await res.arrayBuffer();
        if (previewScanGenByItemRef.current[itemName] !== previewGen) return;

        const fmt = buildFormatForScan(itemsList);
        const { totalPages, successPages, preview, error } = await scanBoxItemAcrossPdfPages(
          buf,
          'template.pdf',
          fmt,
          itemName,
        );
        if (previewScanGenByItemRef.current[itemName] !== previewGen) return;

        let pageExtract = '';
        try {
          pageExtract = await getBoxItemNormalizedTextOnPage(buf, fmt, itemName, target.page || 1);
        } catch {
          pageExtract = '';
        }
        if (previewScanGenByItemRef.current[itemName] !== previewGen) return;

        const pageTrim = (pageExtract || '').replace(/\s+/g, ' ').trim();
        const placementTrim =
          typeof target.placementSample === 'string'
            ? target.placementSample.replace(/\s+/g, ' ').trim()
            : '';
        // Prefer text from the stored box (`textInBox` + `position` / join) — same as send flow.
        // `placementSample` is only the DOM substring at click time and can be a label («NIF»)
        // while the resized box only covers the value; overwriting a good `pageExtract` caused
        // that mismatch (OMM-572).
        const resolvedPreview = routingPreviewAcceptable(itemName, pageTrim)
          ? pageTrim
          : placementTrim && routingPreviewAcceptable(itemName, placementTrim)
            ? placementTrim
            : '';
        const pageLocalTrim = (resolvedPreview || '').replace(/\s+/g, ' ').trim();
        // Do not fall back to `scanBoxItemAcrossPdfPages` `preview` for the toast: that value is
        // the first hit across pages and can show «A08331290» while the box on the current page is
        // empty — then the canvas shows «not found» but the alert still looks successful.
        const alertSamplePreview = pageLocalTrim;

        // The debounced `getRoutingBoxPreviewTexts` effect often finishes before this async scan
        // (especially on large PDFs): it may already set a good `routingItemPreviewText`. This
        // scan's `getBoxItemNormalizedTextOnPage` can still be empty transiently or when
        // `itemsList`/`position` lagged the template — do not wipe a non-empty preview on "done".
        const scanPreviewTrim =
          typeof preview === 'string' ? preview.replace(/\s+/g, ' ').trim() : '';
        setRoutingItemPreviewText(prev => {
          const resolvedTrim = (resolvedPreview || '').replace(/\s+/g, ' ').trim();
          if (routingPreviewAcceptable(itemName, resolvedTrim)) {
            return { ...prev, [itemName]: resolvedPreview };
          }
          const prevTrim =
            typeof prev[itemName] === 'string' ? prev[itemName].replace(/\s+/g, ' ').trim() : '';
          if (successPages > 0 && routingPreviewAcceptable(itemName, prevTrim)) {
            return prev;
          }
          if (successPages > 0 && routingPreviewAcceptable(itemName, scanPreviewTrim)) {
            return { ...prev, [itemName]: scanPreviewTrim };
          }
          return { ...prev, [itemName]: resolvedPreview };
        });

        if (alertGen !== scanGenerationRef.current) return;
        setExtractionScan({
          status: 'done',
          itemName,
          totalPages,
          successPages,
          preview: alertSamplePreview,
          error: !!error,
          placementNote: !!scanExtras.placementNote,
        });
      } catch {
        if (previewScanGenByItemRef.current[itemName] !== previewGen) return;
        if (alertGen !== scanGenerationRef.current) return;
        setExtractionScan({
          status: 'done',
          itemName,
          totalPages: 0,
          successPages: 0,
          preview: '',
          error: true,
          placementNote: !!scanExtras.placementNote,
        });
      }
    },
    [canvasPdfSrc, buildFormatForScan],
  );

  const scheduleExtractionScan = useCallback(
    (itemName, itemsList) => {
      const timers = debounceScanTimersByItemRef.current;
      if (timers[itemName]) clearTimeout(timers[itemName]);
      timers[itemName] = setTimeout(() => {
        delete timers[itemName];
        void runExtractionScan(itemName, itemsList, {});
      }, 450);
    },
    [runExtractionScan],
  );

  /**
   * `position` / `join` come from the initial ghost click (DOM row). After drag/resize, `coords`
   * match the box but `position` can stay wrong — same token list as `getPartsFromPDF` is
   * `textInBox` + `BOX_ITEM_WORD_SPLIT`; re-infer from PDF geometry (OMM-572).
   */
  const refineRoutingPositionAfterCoordsChange = useCallback(
    async (itemName, itemSnapshot) => {
      if (!canvasPdfSrc || itemSnapshot?.coords?.left == null) return;
      const gen = (routingPositionRefineGenByItemRef.current[itemName] ?? 0) + 1;
      routingPositionRefineGenByItemRef.current[itemName] = gen;
      try {
        const res = await fetch(canvasPdfSrc, { credentials: 'same-origin' });
        const buf = await res.arrayBuffer();
        if (routingPositionRefineGenByItemRef.current[itemName] !== gen) return;
        const inferred = await inferRoutingBoxPositionFromViewportBox(
          buf,
          itemSnapshot.page || 1,
          itemSnapshot.coords,
        );
        if (routingPositionRefineGenByItemRef.current[itemName] !== gen) return;
        if (!inferred || inferred.position == null) return;
        let itemsForRescan = null;
        setTemplate(t => {
          const prev = Array.isArray(t.items) ? [...t.items] : [];
          const idx = prev.findIndex(i => i.name === itemName);
          if (idx < 0) return t;
          const cur = prev[idx];
          if (cur.selector !== 'box' || cur.coords?.left == null) return t;
          const oldPos = cur.position != null ? +cur.position : 0;
          const newPos = inferred.position != null ? +inferred.position : 0;
          prev[idx] = { ...cur, position: inferred.position };
          if (newPos !== oldPos) {
            itemsForRescan = prev;
          }
          return { ...t, items: prev };
        });
        // Initial placement scan can finish before `position` is refined from geometry; re-scan so
        // success counts and toast match `getBoxItemNormalizedTextOnPage` (avoids 395/395 + empty preview).
        if (itemsForRescan?.length) {
          queueMicrotask(() => {
            void runExtractionScan(itemName, itemsForRescan, {});
          });
        }
      } catch {
        /* ignore */
      }
    },
    [canvasPdfSrc, runExtractionScan, setTemplate],
  );

  const handlePlace = useCallback(
    payload => {
      const { kind, preset, page } = payload;

      if (kind === 'field') {
        const { dimensions, offsetX, offsetY } = payload;
        if (!dimensions) {
          setPendingPlacement(null);
          return;
        }
        const { type } = preset;
        if (digitalSignatureFieldsMode !== 'enabled') {
          setPendingPlacement(null);
          return;
        }
        const scrollTop = 0;
        const viewportHeight = dimensions.height;
        const base = buildPlacementField(type, {
          currentSignerIndex,
          currentPage: page,
          originalWidth: dimensions.originalWidth,
          originalHeight: dimensions.originalHeight,
          displayWidth: dimensions.width,
          displayHeight: dimensions.height,
          scrollTop,
          viewportHeight,
          offsetX,
          offsetY,
        });
        let next = {
          ...base,
          ...fieldDefaults(type, preset),
        };
        if (preset.prefillValue?.field) {
          const key = preset.prefillValue.field;
          next = {
            ...next,
            name: I18n.t(`Fields.ConfigDialog.dynamicFields.${key}`, { defaultValue: key }),
          };
        }
        syncFieldsToTemplate([...fields, next]);
        if (type === 'signature') {
          setTemplate(t => ({
            ...t,
            isDigitalSignature: true,
            recipientAction: 'sign',
            signingMode: t.signingMode || 'onePerMember',
          }));
        }
        setPendingPlacement(null);
        return;
      }

      if (kind === 'item') {
        const { coords, position, join, recognitionWarning } = payload;
        const routeKey = routingPlacementItemName(preset, template.sendBy);
        if (!coords) {
          return;
        }
        const placementSample =
          typeof payload.selectedText === 'string'
            ? payload.selectedText.replace(/\s+/g, ' ').trim()
            : '';
        const item = {
          name: routeKey,
          selector: 'box',
          coords,
          page,
          position: position != null ? position : 0,
          ...(join ? { join } : {}),
          ...(placementSample && routingPreviewAcceptable(routeKey, placementSample)
            ? { placementSample }
            : {}),
        };
        setTemplate(t => {
          const prev = Array.isArray(t.items) ? [...t.items] : [];
          const hadItems = prev.length > 0;
          const idx = prev.findIndex(i => i.name === routeKey);
          if (idx >= 0) prev[idx] = item;
          else prev.push(item);
          const mergedItems = prev;
          const firstExtractorPlacement = !hadItems && mergedItems.length > 0;
          queueMicrotask(() => {
            void runExtractionScan(routeKey, mergedItems, {
              placementNote: !!recognitionWarning,
            });
            const placed = mergedItems.find(i => i.name === routeKey);
            if (placed?.selector === 'box' && placed.coords?.left != null) {
              void refineRoutingPositionAfterCoordsChange(routeKey, placed);
            }
          });
          return {
            ...t,
            items: mergedItems,
            ...(firstExtractorPlacement ? { split: true } : {}),
          };
        });
        setPendingPlacement(null);
      }
    },
    [
      currentSignerIndex,
      fields,
      refineRoutingPositionAfterCoordsChange,
      runExtractionScan,
      setTemplate,
      syncFieldsToTemplate,
      template.sendBy,
      digitalSignatureFieldsMode,
    ],
  );

  const handleDeleteField = useCallback(
    id => {
      const next = fields.filter(fl => fl.id !== id);
      syncFieldsToTemplate(next);
      if (!next.some(f => f?.type === 'signature')) {
        setTemplate(t => ({
          ...t,
          isDigitalSignature: false,
          recipientAction: 'receive',
        }));
      }
    },
    [fields, setTemplate, syncFieldsToTemplate],
  );

  const handleUpdateRoutingItem = useCallback(
    (name, updates) => {
      let mergedItems = null;
      setTemplate(t => {
        const prev = Array.isArray(t.items) ? [...t.items] : [];
        const idx = prev.findIndex(i => i.name === name);
        if (idx < 0) return t;
        const merged = { ...prev[idx], ...updates };
        if (updates?.coords != null || updates?.page != null) {
          delete merged.placementSample;
        }
        prev[idx] = merged;
        mergedItems = prev;
        return { ...t, items: prev };
      });
      if (mergedItems && (updates?.coords != null || updates?.page != null)) {
        const row = mergedItems.find(i => i.name === name);
        if (row?.selector === 'box') {
          scheduleExtractionScan(name, mergedItems);
          void refineRoutingPositionAfterCoordsChange(name, row);
        }
      }
    },
    [refineRoutingPositionAfterCoordsChange, scheduleExtractionScan, setTemplate],
  );

  const handleDeleteRoutingItem = useCallback(
    name => {
      const timers = debounceScanTimersByItemRef.current;
      if (timers[name]) {
        clearTimeout(timers[name]);
        delete timers[name];
      }
      setExtractionScan(s => (s?.itemName === name ? null : s));
      setRoutingItemPreviewText(prev => {
        const { ...rest } = prev;
        return rest;
      });
      setTemplate(t => ({
        ...t,
        items: (t.items || []).filter(i => i.name !== name),
        ...(name === 'dni' ? { encryptPdfWithDni: false } : {}),
      }));
    },
    [setTemplate],
  );

  if (!canvasPdfSrc) {
    return (
      <StepPdfSource
        template={template}
        uploadBusy={uploadBusy}
        onPdfDrop={onPdfDrop}
        canvasPdfSrc={canvasPdfSrc}
        onReplacePdf={onReplacePdf}
        copyVariant={sendSessionPdfGate ? 'sendSession' : 'editor'}
      />
    );
  }

  const extractionAlert =
    extractionScan &&
    (() => {
      if (extractionScan.status === 'hint') {
        return (
          <Alert
            severity="info"
            sx={{ py: 0.5 }}
            onClose={() => {
              scanGenerationRef.current += 1;
              setExtractionScan(null);
            }}
          >
            {I18n.t('HrBot.EditTemplate.SetupDocument.extractionScan.dragHint')}
          </Alert>
        );
      }
      if (extractionScan.status === 'selectionError') {
        const messageKey =
          extractionScan.reason === 'multiline'
            ? 'HrBot.EditTemplate.SetupDocument.extractionScan.selectionMultiline'
            : 'HrBot.EditTemplate.SetupDocument.extractionScan.selectionEmpty';
        return (
          <Alert
            severity="warning"
            sx={{ py: 0.5 }}
            onClose={() => {
              scanGenerationRef.current += 1;
              setExtractionScan(null);
            }}
          >
            {I18n.t(messageKey)}
          </Alert>
        );
      }
      if (extractionScan.status === 'loading') {
        const { itemName } = extractionScan;
        const slotLabel = extractionPaletteLabel(itemName);
        return (
          <Alert
            severity="info"
            sx={{ py: 0.5 }}
            onClose={() => {
              scanGenerationRef.current += 1;
              setExtractionScan(null);
            }}
          >
            <Box
              component="span"
              sx={{ display: 'block', typography: 'caption', color: 'text.secondary', mb: 0.25 }}
            >
              {slotLabel}
            </Box>
            {I18n.t('HrBot.EditTemplate.SetupDocument.extractionScan.loading')}
          </Alert>
        );
      }
      if (extractionScan.status === 'done') {
        const { totalPages, successPages, preview, error, placementNote, itemName } =
          extractionScan;
        const slotLabel = extractionPaletteLabel(itemName);
        if (error) {
          return (
            <Alert severity="error" sx={{ py: 0.5 }} onClose={() => setExtractionScan(null)}>
              {I18n.t('HrBot.EditTemplate.SetupDocument.extractionScan.error')}
            </Alert>
          );
        }
        const multiPage = totalPages > 1;
        // `extractionScan.preview` is frozen at scan end; debounced `getRoutingBoxPreviewTexts` may
        // update `routingItemPreviewText` a moment later — toast must match the canvas (same map).
        const previewFromScan = typeof preview === 'string' ? preview : '';
        const previewFromCanvasRaw = routingItemPreviewText[itemName];
        const canvasTrim =
          typeof previewFromCanvasRaw === 'string'
            ? previewFromCanvasRaw.replace(/\s+/g, ' ').trim()
            : '';
        const scanTrim = (previewFromScan ?? '').replace(/\s+/g, ' ').trim();
        // Do not let an empty debounced map entry override the frozen scan preview (shows warning
        // + "395/395 pages" while the sample from the scan was non-empty).
        const raw =
          canvasTrim.length > 0
            ? previewFromCanvasRaw
            : scanTrim.length > 0
              ? previewFromScan
              : previewFromCanvasRaw;
        const previewTrim = (raw ?? '').replace(/\s+/g, ' ').trim();
        const pageLocalOk = routingPreviewAcceptable(itemName, previewTrim);
        const scanOk = successPages > 0;
        // Canvas + `routingItemDetection` use page-local preview only. For a single-page PDF,
        // do not treat cross-page `scanOk` as success when this page's box is empty.
        const mainKey = multiPage
          ? scanOk
            ? pageLocalOk
              ? 'HrBot.EditTemplate.SetupDocument.extractionScan.pagesFound'
              : 'HrBot.EditTemplate.SetupDocument.extractionScan.pagesFoundNoLocalPreview'
            : 'HrBot.EditTemplate.SetupDocument.extractionScan.noneMulti'
          : pageLocalOk
            ? 'HrBot.EditTemplate.SetupDocument.extractionScan.singleFound'
            : 'HrBot.EditTemplate.SetupDocument.extractionScan.noneSingle';
        const main = I18n.t(mainKey, {
          success: successPages,
          total: totalPages,
        });
        const sample =
          previewTrim && pageLocalOk
            ? I18n.t('HrBot.EditTemplate.SetupDocument.extractionScan.sample', {
                preview: previewTrim,
              })
            : null;
        return (
          <Alert
            severity={pageLocalOk ? 'success' : 'warning'}
            sx={{ py: 0.5 }}
            onClose={() => setExtractionScan(null)}
          >
            <Box
              component="span"
              sx={{ display: 'block', typography: 'caption', color: 'text.secondary', mb: 0.25 }}
            >
              {slotLabel}
            </Box>
            <Box component="span" sx={{ display: 'block' }}>
              {main}
            </Box>
            {sample && (
              <Box component="span" sx={{ display: 'block', mt: 0.5, typography: 'caption' }}>
                {sample}
              </Box>
            )}
            {placementNote && (
              <Box component="span" sx={{ display: 'block', mt: 0.5, typography: 'caption' }}>
                {I18n.t('HrBot.EditTemplate.SetupDocument.extractionScan.placementNote')}
              </Box>
            )}
          </Alert>
        );
      }
      return null;
    })();

  const floatingScanAlert =
    extractionAlert &&
    typeof document !== 'undefined' &&
    createPortal(
      <Box
        sx={{
          position: 'fixed',
          left: '16px',
          right: '16px',
          bottom: 'max(16px, env(safe-area-inset-bottom, 0px))',
          zIndex: theme.zIndex.snackbar,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            pointerEvents: 'auto',
            width: 'fit-content',
            maxWidth: 'min(720px, calc(100vw - 32px))',
            '& .MuiAlert-root': { boxShadow: 8 },
          }}
        >
          {extractionAlert}
        </Box>
      </Box>,
      document.body,
    );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minHeight: 480,
      }}
    >
      {floatingScanAlert}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
          gap: 2,
          flex: 1,
          minHeight: 0,
        }}
      >
        <SetupDocumentPalette
          pendingPlacement={pendingPlacement}
          setPendingPlacement={setPendingPlacement}
          encryptPdfWithDni={!!template.encryptPdfWithDni}
          collectiveSigningMode={signingMode !== 'onePerMember'}
          canUseXml={canUseXml}
          template={template}
          setTemplate={setTemplate}
          showSharedSigningToolbar={showSharedSigningToolbar}
          ommnioSignMarketingUrl={ommnioSignMarketingUrl}
          digitalSignatureFieldsMode={digitalSignatureFieldsMode}
          fields={fields}
          placeholderMembers={placeholderMembers}
          currentSignerIndex={currentSignerIndex}
          setCurrentSignerIndex={setCurrentSignerIndex}
          syncFieldsToTemplate={syncFieldsToTemplate}
        />
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 420,
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
          }}
        >
          <WidgetCanvas
            src={canvasPdfSrc}
            fields={fields}
            onAddField={() => {}}
            onUpdateField={(fieldId, patch) =>
              syncFieldsToTemplate(fields.map(fl => (fl.id === fieldId ? { ...fl, ...patch } : fl)))
            }
            onDeleteField={handleDeleteField}
            routingItems={template.items || []}
            routingItemPreviewText={routingItemPreviewText}
            routingItemDetection={routingItemDetection}
            onUpdateRoutingItem={handleUpdateRoutingItem}
            onDeleteRoutingItem={handleDeleteRoutingItem}
            pendingPlacement={pendingPlacement}
            onPlace={handlePlace}
            onCancelPlacement={() => setPendingPlacement(null)}
          />
        </Box>
      </Box>
    </Box>
  );
};

StepSetupDocument.propTypes = {
  template: object.isRequired,
  setTemplate: func.isRequired,
  fields: array.isRequired,
  syncFieldsToTemplate: func.isRequired,
  canvasPdfSrc: string,
  uploadBusy: bool,
  onPdfDrop: func.isRequired,
  onReplacePdf: func.isRequired,
  currentSignerIndex: number.isRequired,
  setCurrentSignerIndex: func.isRequired,
  placeholderMembers: array.isRequired,
  canUseXml: bool,
  sendSessionPdfGate: bool,
};

export default StepSetupDocument;
